import React, { useState, useRef, useEffect } from 'react';
import './ExpandableSearch.css';

const ExpandableSearch = ({ value, onChange, placeholder = "Search players..." }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [inputWidth, setInputWidth] = useState(300);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  // Opened because the value was set from outside, not by the user: don't focus the input
  const openedFromValue = useRef(false);

  useEffect(() => {
    if (value && !isExpanded) {
      openedFromValue.current = true;
      setIsExpanded(true);
    }
  }, [value, isExpanded]);

  useEffect(() => {
    if (isExpanded && inputRef.current) {
      if (!openedFromValue.current) {
        inputRef.current.focus();
      }
      openedFromValue.current = false;

      // Calculate the width based on container position
      if (containerRef.current) {
        const container = containerRef.current.closest('.selection-container');
        if (container) {
          const containerRect = container.getBoundingClientRect();
          const buttonRect = containerRef.current.getBoundingClientRect();
          const availableWidth = buttonRect.right - containerRect.left - 5; // 10px from left edge
          setInputWidth(availableWidth);
        }
      }
    }
  }, [isExpanded]);

  const handleToggle = () => {
    if (isExpanded && value) {
      // If expanded with text, clear it first
      onChange('');
    } else if (isExpanded) {
      // If expanded without text, collapse
      setIsExpanded(false);
    } else {
      // If collapsed, expand
      setIsExpanded(true);
    }
  };

  const handleInputChange = (e) => {
    onChange(e.target.value);
  };

  const handleBlur = (e) => {
    // Only collapse if clicking outside the entire component
    if (!e.currentTarget.contains(e.relatedTarget) && !value) {
      setIsExpanded(false);
    }
  };

  return (
    <div
      ref={containerRef}
      className={`expandable-search ${isExpanded ? 'expanded' : ''}`}
      onBlur={handleBlur}
    >
      <input
        ref={inputRef}
        type="text"
        className="expandable-search-input"
        style={{ width: isExpanded ? `${inputWidth}px` : '0' }}
        value={value}
        onChange={handleInputChange}
        placeholder={placeholder}
      />
      <button
        className="expandable-search-button"
        onClick={handleToggle}
        type="button"
        aria-label={isExpanded && value ? "Clear search" : isExpanded ? "Close search" : "Search players"}
      >
        {isExpanded && value ? (
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 4L4 12M4 4L12 12"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        ) : (
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle
              cx="7"
              cy="7"
              r="5"
              stroke="currentColor"
              strokeWidth="2"
              fill="none"
            />
            <path
              d="M11 11L14 14"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        )}
      </button>
    </div>
  );
};

export default ExpandableSearch;